// AI 기반 요약 템플릿 생성 서비스

class AISummaryService {
  constructor() {
    // 보안: API 키를 클라이언트에서 제거하고 서버 API Route 사용
    this.provider = process.env.REACT_APP_AI_PROVIDER || 'openai'; // 'openai' or 'gemini'
    this.apiEndpoint = '/api/ai'; // Vercel API Route
  }

  // AI 서비스 가용성 체크
  get isAvailable() {
    // 클라이언트에서는 환경변수로 프로바이더 설정 여부만 확인
    return !!process.env.REACT_APP_AI_PROVIDER;
  }

  // 학습 내용을 AI로 요약하여 템플릿 생성
  async generateSummaryTemplate(chunkData) {
    try {
      const prompt = this.buildPrompt(chunkData);
      const response = await this.callAIAPI(prompt);
      return this.parseSummaryResponse(response);
      
    } catch (error) {
      console.error('AI 요약 생성 실패:', error);
      return this.generateFallbackTemplate(chunkData);
    }
  }

  // 프롬프트 구성
  buildPrompt(chunkData) {
    const text = chunkData.text;
    const topic = chunkData.topic_title;
    const lesson = chunkData.lesson_title;
    
    return `
다음은 NCS(국가직무능력표준) 정보처리기사 교육 내용입니다.

**학습 단위**: ${lesson}
**토픽**: ${topic}
**내용**: ${text}

위 내용을 분석하여 다음 형식으로 정리해주세요:

{
  "핵심개념": "한 문장으로 핵심 개념 요약",
  "상세설명": "2-3문장으로 상세 설명",
  "키워드": ["키워드1", "키워드2", "키워드3", "키워드4", "키워드5"],
  "내용유형": ["정의", "절차", "예시", "이론", "실무"] 중 해당하는 것들,
  "중요도": "상/중/하",
  "연관개념": ["연관된 다른 개념들"],
  "실무활용": "실무에서 어떻게 활용되는지",
  "예시": "구체적인 예시나 사례",
  "체크포인트": ["학습자가 확인해야 할 포인트들"]
}

JSON 형식으로만 응답해주세요.
`;
  }

  // Gemini API 직접 호출 (개발 환경용)
  async callAIAPI(prompt, responseType = 'default') {
    try {
      // Vercel API Route만 사용 (보안)
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: prompt,
          provider: this.provider,
          responseType: responseType
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`AI 서비스 오류: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'AI 서비스에서 오류가 발생했습니다.');
      }

      return data.response;
      
    } catch (error) {
      console.error('AI API 호출 실패:', error);
      throw error;
    }
  }

  // AI 응답 파싱
  parseSummaryResponse(response) {
    try {
      // JSON 추출 (마크다운 코드 블록 제거)
      const jsonMatch = response.match(/```json\n([\s\S]*?)\n```/) || response.match(/\{[\s\S]*\}/);
      const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : response;
      
      const parsed = JSON.parse(jsonStr);
      
      // 기본 검증 및 정제
      return {
        핵심개념: parsed.핵심개념 || '',
        상세설명: parsed.상세설명 || '',
        키워드: Array.isArray(parsed.키워드) ? parsed.키워드 : [],
        내용유형: Array.isArray(parsed.내용유형) ? parsed.내용유형 : ['학습내용'],
        중요도: parsed.중요도 || '중',
        연관개념: Array.isArray(parsed.연관개념) ? parsed.연관개념 : [],
        실무활용: parsed.실무활용 || '',
        예시: parsed.예시 || '',
        체크포인트: Array.isArray(parsed.체크포인트) ? parsed.체크포인트 : []
      };
      
    } catch (error) {
      console.error('AI 응답 파싱 실패:', error);
      throw error;
    }
  }

  // AI 실패시 폴백 템플릿
  generateFallbackTemplate(chunkData) {
    const text = chunkData.text;
    const keywords = this.extractBasicKeywords(text);
    
    return {
      핵심개념: text.substring(0, 100) + '...',
      상세설명: text.substring(0, 200) + '...',
      키워드: keywords,
      내용유형: this.guessContentType(text),
      중요도: '중',
      연관개념: [],
      실무활용: '정보처리기사 실무에 활용됩니다.',
      예시: '',
      체크포인트: ['핵심 개념 이해', '실무 적용 방법 확인']
    };
  }

  // 기본 키워드 추출
  extractBasicKeywords(text) {
    const techTerms = text.match(/[가-힣]{2,}(?:시스템|모델|방법|기법|분석|설계|구현|테스트|관리|요구사항|인터페이스|아키텍처|소프트웨어|하드웨어|네트워크|데이터베이스|프로그래밍|알고리즘)/g) || [];
    return [...new Set(techTerms)].slice(0, 5);
  }

  // 내용 유형 추측
  guessContentType(text) {
    const types = [];
    
    if (text.includes('정의') || text.includes('개념') || text.includes('이란')) {
      types.push('정의');
    }
    if (text.includes('절차') || text.includes('단계') || text.includes('과정')) {
      types.push('절차');
    }
    if (text.includes('예시') || text.includes('예제') || text.includes('사례')) {
      types.push('예시');
    }
    if (text.includes('이론') || text.includes('원리')) {
      types.push('이론');
    }
    
    return types.length > 0 ? types : ['학습내용'];
  }

  // 여러 청크를 배치로 처리
  async generateBatchSummaries(chunks, onProgress) {
    const results = [];
    const total = chunks.length;
    
    for (let i = 0; i < chunks.length; i++) {
      try {
        const summary = await this.generateSummaryTemplate(chunks[i]);
        results.push({
          chunkId: chunks[i].chunk_id,
          summary: summary,
          success: true
        });
        
        // 진행률 콜백
        if (onProgress) {
          onProgress(i + 1, total);
        }
        
        // API 레이트 리밋 방지
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (error) {
        console.error(`청크 ${chunks[i].chunk_id} 처리 실패:`, error);
        results.push({
          chunkId: chunks[i].chunk_id,
          summary: this.generateFallbackTemplate(chunks[i]),
          success: false,
          error: error.message
        });
      }
    }
    
    return results;
  }

  // 일반적인 AI 텍스트 생성 (면접 질문, 피드백 등용)
  async generateSummary(prompt, responseType = 'default') {
    try {
      return await this.callAIAPI(prompt, responseType);
    } catch (error) {
      console.error('AI 텍스트 생성 실패:', error);
      throw error;
    }
  }

  // API 키 유효성 확인 (서버에서 확인)
  async validateAPIKey() {
    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: 'test',
          provider: this.provider
        })
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

// 싱글톤 인스턴스
const aiSummaryService = new AISummaryService();

export default aiSummaryService;